// src/hooks/useLinkedCoverImage.js
//
// The front-cover image a manuscript should show in a preview, or null.
//
// A manuscript reaches its cover one of two ways — it carries its own
// `coverVersions`, or it has a `linkedCoverId` pointing at a separate cover-only
// draft — and neither payload is enough on its own: `GET /api/drafts/<id>`
// strips every design blob and returns `coverImage` as a raw S3 key rather than
// a URL. So both shapes resolve through the same request to the cover draft's
// versions endpoint, which is also the only place that knows which version is
// the book's primary (see `get_cover_versions` in backend/routes/bookcover.py —
// explicit primary, then the assigned cover, then the active version).
//
// Deliberately uses the stored `cover_url` and never renders the Fabric design
// client-side. The designer already uploads a front-face JPEG on every save, and
// a design whose images were offloaded to S3 taints an offscreen canvas on
// export — the cover designer hits exactly that and has to catch it.
//
// Null is a first-class answer: no link, no permission on the cover draft, no
// saved image, a failed request. Every one of them means "draw the placeholder".

import { useEffect, useState } from 'react';
import { API_BASE } from '../utils/api';
import { fromCoverKey } from '../config/bookSizes';

// A stable identity, so callers can depend on the result without re-rendering
// every time the hook decides it has nothing.
const EMPTY = { url: null, backUrl: null, trimSize: null, versionName: null };

/**
 * @param {object|null} draft - the manuscript draft (needs `_id`, `linkedCoverId`)
 * @param {boolean} enabled - only fetch while the preview is actually open; the
 *   presigned URL lasts an hour and an editor session lasts much longer, so this
 *   must be tied to opening the preview rather than mounting the page.
 * @returns {{url: string|null, backUrl: string|null, trimSize: string|null,
 *            versionName: string|null}}
 *   `backUrl` is the back face. The designer's explicit save uploads three
 *   cropped regions of the wraparound — front, spine and back — so a cover
 *   saved that way has a real back cover to show. The autosave thumbnail
 *   writes the front only, so `backUrl` is frequently null while `url` is not,
 *   and the two are reported separately rather than assumed to arrive together.
 *   `trimSize` is the canonical id the cover was DESIGNED at, which is not
 *   necessarily the one the manuscript is set to — a cover and an interior of
 *   different sizes cannot be bound into one book, so the caller needs both to
 *   be able to say so.
 */
export default function useLinkedCoverImage(draft, enabled) {
  const [result, setResult] = useState(EMPTY);

  // Which draft holds this book's cover is the SERVER's answer, not one worked
  // out here. A manuscript is meant to have a cover one way or the other, but
  // older data can have both, and this used to prefer the linked one while the
  // Cover Designer button preferred the manuscript's own — so the preview
  // showed one cover and the button opened another. `effectiveCoverDraftId`
  // is that question answered once, in `effective_cover_draft_id`.
  //
  // The fallbacks are for a payload that predates the field: the old behaviour,
  // so nothing breaks before the backend ships.
  const coverDraftId = draft?.effectiveCoverDraftId
    || draft?.linkedCoverId
    || draft?._id
    || null;

  useEffect(() => {
    if (!enabled || !coverDraftId) {
      setResult(EMPTY);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const token = localStorage.getItem('rv_access_token');
        const res = await fetch(
          `${API_BASE}/api/drafts/${coverDraftId}/cover/versions`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        // 403 is ordinary here, not an error worth surfacing: a manuscript
        // collaborator who is not on the cover draft cannot read it, and the
        // placeholder is the right thing for them to see.
        if (!res.ok) return;

        const data = await res.json();
        const versions = data.versions || [];
        const primary = versions.find((v) => v.id === data.primaryVersionId);

        if (cancelled) return;
        setResult(primary
          ? {
              url: primary.cover_url || null,
              backUrl: primary.back_url || null,
              trimSize: fromCoverKey(primary.bookSize),
              versionName: primary.name || null,
            }
          : EMPTY);
      } catch {
        // Offline, aborted, malformed — all mean "no cover to show".
      }
    })();

    return () => { cancelled = true; };
  }, [coverDraftId, enabled]);

  return result;
}
